import { useEffect, useState } from 'react';
import { NamePromptModal } from './namePromptModal';
import { PaperAirplaneIcon } from '@heroicons/react/16/solid';
import {
  ProfileResponse,
  usePostInteractionsByTripByTripIdAndLinkSecretCommentMutation,
} from '../../../store/backendApi';
import { FailedNotification } from './failedNotification';

export interface CommentInputProps {
  tripId: string;
  storyId: string;
  linkSecret: string | null;
  profile?: ProfileResponse | null;
}

const NAME_KEY = 'tripvapp_c_name';

export const CommentInput = (props: CommentInputProps) => {
  const isLoggedIn = !!props.profile?.userId;

  const [name, setName] = useState<string | null>(() => {
    return localStorage.getItem(NAME_KEY) || '';
  });
  const [comment, setComment] = useState<string | null>(null);
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [showFailureNotification, setShowFailureNotification] = useState(false);

  const sendDisabled = !comment || comment.length < 2;

  const [postComment, { isError, isSuccess }] =
    usePostInteractionsByTripByTripIdAndLinkSecretCommentMutation();

  const submit = async () => {
    if (!name || !comment) {
      return;
    }
    if (!isLoggedIn) {
      localStorage.setItem(NAME_KEY, name);
    }

    const commentBodyRequest =
      isLoggedIn && props.profile?.userId
        ? {
          storyId: props.storyId,
          userId: props.profile.userId,
          body: comment,
        }
        : {
          storyId: props.storyId,
          name: name,
          body: comment,
        };

    postComment({
      tripId: props.tripId,
      linkSecret: props.linkSecret ?? 'none',
      commentBodyRequest,
    });
  };

  useEffect(() => {
    if (isLoggedIn && props.profile?.username) {
      setName(props.profile.username);
    }
  }, [isLoggedIn, props.profile?.username]);

  useEffect(() => {
    if (isError) {
      setShowFailureNotification(true);
    }
  }, [isError]);

  useEffect(() => {
    if (isSuccess) {
      setComment(null);
      setNamePromptOpen(false);
    }
  }, [isSuccess]);

  return (
    <>
      <FailedNotification
        show={showFailureNotification}
        setShow={setShowFailureNotification}
        title="Could not send comment"
        body={'Try again later'}
      />
      <div className="w-full relative flex items-start gap-2">
        <textarea
          rows={2}
          value={comment ?? ''}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1
          -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2
          focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
          placeholder="Add a comment...."
        />
        <button
          type="button"
          disabled={sendDisabled}
          onClick={() => {
            if (isLoggedIn) {
              submit();
            } else {
              setNamePromptOpen(true);
            }
          }}
          className="h-10 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-1.5
          text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2
        focus-visible:outline-offset-2 disabled:bg-indigo-300 disabled:cursor-not-allowed
         focus-visible:outline-indigo-600"
        >
          Send
          <PaperAirplaneIcon aria-hidden="true" className="-mr-0.5 size-5" />
        </button>

        <NamePromptModal
          open={namePromptOpen}
          setOpen={setNamePromptOpen}
          name={name}
          setName={setName}
          sendClicked={submit}
        />
      </div>
    </>
  );
};
